import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformServer } from '@angular/common';

import Debug from 'debug';
import * as Sentry from '@sentry/browser';

import * as Cache from 'in-memoriam'; 
import { User } from '../types/user';
import { Stream } from '../types/stream';
import { Note } from '../types/note';

const debug = Debug('notd:shared:cache_service');

@Injectable({
  providedIn: 'root' 
})
export class CacheService {
  public cache: Cache.Cache; 

  private cacheKeys = {
    channel: 'channel:',
    myStreams: 'myStreams',
    stream: 'stream',
    streamPlans: 'streamPlans',
    streamsFollowed: 'profile:following',
    userStreams: 'userStreams',
    userProfile: 'uProfile',
    userProfileByUsername: 'userNameProfile',
    purchaseToken: 'purchased:',
    post: 'note:',
    postLead: 'postLead:',
    countryList: 'countryList',
    userCachesList: 'userCachesList',
    streamPurchaseToken: 'streamPurchaseToken',
    team: 'team',
    teamPlans: 'teamPlans',
    countryStates: 'countryStates',
    userLocation: 'userLocation',
    noteTags: 'defaultNoteTags',
    noteStream: 'defaultNoteStream',
    followedStreams: 'followedStreams',
    userTeamStreams: 'userTeamStreams',
    notifications: 'notifications'
  };

  private platformId = inject(PLATFORM_ID);

  constructor() { 
    const capacity = 1000; 
    const ttl = 100000; 
    this.cache = new (Cache.default || Cache)(capacity, ttl); 
  }

  clearUserCache(): void {
    const userKeys: Record<string, boolean> = this.cache.get(this.cacheKeys.userCachesList) || {};
    for (const key of Object.keys(userKeys)) {
      this.cache.remove(key);
    }
    this.setCache(this.cacheKeys.userCachesList, {});
  }

  clearCachedPost(noteId: string): void {
    this.cache.remove(this.cacheKeys.post + noteId);
  }

  public setCache(key: string, value: any): void {
    if (isPlatformServer(this.platformId)) {
      return;
    }

    try {
      this.cache.set(key, value);
    } catch (ex) {
      Sentry.captureException(ex, {
        tags: {
          section: 'CacheService',
          key
        },
      });
      this.clearCache();
    }
  }

  setUserProfileCache(profileData: User): void {
    if (profileData?.id) {
      this.setCache(`${this.cacheKeys.userProfile}:${profileData.id}`, profileData);
    }
  }

  setUserProfileByUsernameCache(profileData: User): void {
    if (profileData?.id) {
      this.setCache(`${this.cacheKeys.userProfileByUsername}:${profileData.id}`, profileData);
    }
  }

  getUserProfileCache(userId: string | number): User | undefined {
    return this.cache.get(`${this.cacheKeys.userProfile}:${userId}`);
  }

  getUserProfileByUsernameCache(username: string): User | undefined {
    return this.cache.get(`${this.cacheKeys.userProfileByUsername}:${username}`);
  }

  removeUserProfileCache(profileData: User): void {
    this.cache.remove(`${this.cacheKeys.userProfile}:${profileData.id}`);
  }

  removeUserProfileByUsernameCache(profileData: User): void {
    this.cache.remove(`${this.cacheKeys.userProfileByUsername}:${profileData.id}`);
  }

  getChannelInfo(channelId: string): Stream | undefined {
    return this.cache.get(this.cacheKeys.channel + channelId);
  }

  setChannelInfo(channelId: string, channelData: Stream): void {
    this.setCache(this.cacheKeys.channel + channelId, this.cloneObj(channelData));
  }

  getStreamsFollowed(): any {
    return this.cache.get(this.cacheKeys.streamsFollowed);
  }

  setStreamsFollowed(channelsFollowedData: any): void {
    this.setCache(this.cacheKeys.streamsFollowed, this.cloneObj(channelsFollowedData));
  }

  getPostPurchaseToken(postId: string): string | undefined {
    return this.cache.get(this.cacheKeys.purchaseToken + postId);
  }

  setPostPurchaseToken(postId: string, token: string): void {
    const key = this.cacheKeys.purchaseToken + postId;
    const userKeys: Record<string, boolean> = this.cache.get(this.cacheKeys.userCachesList) || {};
    userKeys[key] = true;
    this.setCache(this.cacheKeys.userCachesList, userKeys);
    this.setCache(key, token);
  }

  getStreamPurchaseToken(streamId: string): string | undefined {
    return this.cache.get(this.cacheKeys.streamPurchaseToken + streamId);
  }

  setStreamPurchaseToken(streamId: string, token: string): void {
    const key = this.cacheKeys.streamPurchaseToken + streamId;
    this.setCache(key, token);
  }

  getPostInfo(postId: number | string): Note | undefined {
    debug('getNoteInfo: ' + postId, this.cache.get(this.cacheKeys.post + postId));
    return this.cache.get(`${this.cacheKeys.post}:${postId}`);
  }

  setPostInfo(postId: number | string, postData: Note): void {
    debug('setPostInfo: ' + postId, postData);
    this.setCache(`${this.cacheKeys.post}:${postId}`, postData);
  }

  getPostLead(postId: number | string): any {
    return this.cache.get(this.cacheKeys.postLead + postId);
  }

  setPostLead(postId: number | string, postData: any): void {
    if (postId && postData) {
      this.setCache(this.cacheKeys.postLead + postId, this.cloneObj(postData));
    }
  }

  getCountryList(): any {
    return this.cache.get(this.cacheKeys.countryList);
  }

  setCountryList(countryList: any): void {
    this.setCache(this.cacheKeys.countryList, this.cloneObj(countryList));
  }

  getCountryStates(countryId: string): any {
    const states: any = this.cache.get(this.cacheKeys.countryStates);
    return states?.[countryId];
  }

  setCountryStates(countryId: string, states: any): void {
    let cachedPlans = this.cache.get(this.cacheKeys.countryStates) || {};
    cachedPlans[countryId] = this.cloneObj(states);
    this.setCache(this.cacheKeys.countryStates, cachedPlans);
  }

  setUserLocation(location: any): void {
    this.setCache(this.cacheKeys.userLocation, location);
  }

  getUserStreams(): any {
    return this.cache.get(this.cacheKeys.userStreams);
  }

  setUserStreams(userStreams: any): void {
    this.setCache(this.cacheKeys.userStreams, this.cloneObj(userStreams));
  }

  clearUserStreams(): void {
    this.cache.remove(this.cacheKeys.userStreams);
  }

  getStreamInfo(streamId: string | number): Stream | undefined {
    return this.cache.get(`${this.cacheKeys.stream}:${streamId}`);
  }

  setStreamInfo(stream: Stream): void {
    if (this.getStreamInfo(stream.id)) {
      this.cache.remove(`${this.cacheKeys.stream}:${stream.id}`);
    }
    this.setCache(`${this.cacheKeys.stream}:${stream.id}`, stream);
  }

  getFollowedStreams(): Stream[] | null | undefined {
    return this.cache.get(this.cacheKeys.followedStreams);
  }

  setFollowedStreams(streams: Stream[] | any): void {
    this.setCache(this.cacheKeys.followedStreams, streams);
  }

  getSubscriptionPlans(streamId: string): any {
    const plans: Record<string, any> = this.cache.get(this.cacheKeys.streamPlans);
    return plans?.[streamId];
  }

  setSubscriptionPlans(streamId: string, plans: any[]): void {
    let cachedPlans: Record<string, any[]> = this.cache.get(this.cacheKeys.streamPlans) || {};
    cachedPlans[streamId] = this.cloneObj(plans);
    this.setCache(this.cacheKeys.streamPlans, cachedPlans);
  }

  getMyStreams(): any {
    return this.cache.get(this.cacheKeys.myStreams);
  }

  setMyStreams(myStreams: any): void {
    this.setCache(this.cacheKeys.myStreams, this.cloneObj(myStreams));
  }

  clearMyStreams(): void {
    this.cache.remove(this.cacheKeys.myStreams);
  }

  getTeam(): any {
    return this.cache.get(this.cacheKeys.team);
  }

  setTeam(team: any): void {
    this.setCache(this.cacheKeys.team, this.cloneObj(team));
  }

  clearTeam(): void {
    this.cache.remove(this.cacheKeys.team);
  }

  getTeamSubscriptionPlans(teamId: string): any {
    const plans: Record<string, any> = this.cache.get(this.cacheKeys.teamPlans);
    return plans?.[teamId];
  }

  setTeamSubscriptionPlans(teamId: string, plans: any[]): void {
    let cachedPlans: Record<string, any[]> = this.cache.get(this.cacheKeys.teamPlans) || {};
    cachedPlans[teamId] = this.cloneObj(plans);
    this.setCache(this.cacheKeys.teamPlans, cachedPlans);
  }

  getUserTeamStreams(): any {
    return this.cache.get(this.cacheKeys.userTeamStreams);
  }

  setUserTeamStreams(cachedData: { users: User[]; channels: Stream[] }): void {
    this.setCache(this.cacheKeys.userTeamStreams, cachedData);
  }

  setDefaultNoteStream(streamId: string | number): void {
    this.cache.remove(this.cacheKeys.noteStream);
    this.setCache(this.cacheKeys.noteStream, streamId);
  }

  getDefaultNoteStream(): string | number | undefined {
    return this.cache.get(this.cacheKeys.noteStream);
  }

  setDefaultNoteTags(tagsObject: any): void {
    this.cache.remove(this.cacheKeys.noteTags);
    this.setCache(this.cacheKeys.noteTags, tagsObject);
  }

  getNoteTags(): any {
    return this.cache.get(this.cacheKeys.noteTags);
  }

  getNotifications(): any {
    return this.cache.get(this.cacheKeys.notifications);
  }

  public setNotifications(notifications: any): void {
    this.clearNotifications();
    this.setCache(this.cacheKeys.notifications, notifications);
  }

  public clearNotifications(): void {
    this.cache.remove(this.cacheKeys.notifications);
  }

  clearCache(): void {
    Object.values(this.cacheKeys).forEach(key => {
      if (key.endsWith(':') === false) {
        this.cache.remove(key);
      }
    });
  }

  private cloneObj(object: any): any {
    return JSON.parse(JSON.stringify(object));
  }
}